import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';

import { StorageService } from '../';
import { AuthService } from '../AuthService';
import { IAuthUser } from '../../types';
import * as Sentry from '@sentry/react';
import ImpersonationService from '../../common/services/ImpersonationService';
import { OAuthLoginPayload } from '../../actions/AuthActions';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const UserService = {
    authUser,
    setCustomer,
    logout,
    isLogged,
    hasExpired,
    isAdmin,
    getAccessToken,
    getRefreshToken,
    getUserId,
    getActiveQuestionnaires,
    resetPassword,
    requestResetPassword,
    changePassword,
    authUserWithToken,
    authUserWithOAuth,
    isAnonymous,
};

async function authUser(user: IAuthUser) {
    return await RequestService.post('/auth/login', user, {
        ignoreAuth: true,
    });
}

function setCustomer(accessToken: string, refreshToken: string) {
    StorageService.setLocal(ACCESS_TOKEN_KEY, accessToken);
    StorageService.setLocal(REFRESH_TOKEN_KEY, refreshToken);

    const data = AuthService.decodeUserData(accessToken);
    if (!data) {
        return;
    }

    // companyId is tagged separately once /config resolves it (see listenerMiddleware)
    Sentry.setUser({ id: data.sub });
}

async function logout() {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        deleteLocalData();
        return;
    }

    try {
        await RequestService.post('/auth/logout', { refreshToken });
    } catch (ex) {
        Sentry.captureException(ex);
    } finally {
        // Another tab may have stored a new pair while the request was in flight; that session stays.
        if (getRefreshToken() === refreshToken) {
            deleteLocalData();
        }
    }

    function deleteLocalData() {
        StorageService.deleteLocal(ACCESS_TOKEN_KEY);
        StorageService.deleteLocal(REFRESH_TOKEN_KEY);
        ImpersonationService.clear();

        Sentry.setUser(null);
    }
}

function isLogged(): boolean {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    return !!accessToken && !!refreshToken;
}

function hasExpired(): boolean {
    const accessToken = getAccessToken();
    return !!accessToken && AuthService.isAccessTokenExpired(accessToken);
}

function isAdmin(): boolean {
    const accessToken = getAccessToken();
    return AuthService.isUserAdmin(accessToken);
}

function getUserId(): number {
    const accessToken = getAccessToken();
    return AuthService.getKey(accessToken, 'sub');
}

function getAccessToken(): string {
    return StorageService.getLocal<string>(ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string {
    return StorageService.getLocal<string>(REFRESH_TOKEN_KEY);
}

async function getActiveQuestionnaires() {
    return await RequestService.get('/participant/active-questionnaires');
}

async function requestResetPassword(email: string) {
    return await RequestService.post(
        '/request-reset-password',
        {
            email,
        },
        { ignoreAuth: true },
    );
}

async function resetPassword(payload: {
    controlPassword: string;
    password: string;
}) {
    const { controlPassword, password } = payload;

    return await RequestService.post(
        '/reset-password',
        {
            controlPassword,
            password,
        },
        { ignoreAuth: true },
    );
}

type ChangePasswordPayload = {
    controlPassword: string;
    password: string;
    userId: number;
};

async function changePassword(
    payload: ChangePasswordPayload,
): Promise<RequestServiceResponse<unknown>> {
    const { controlPassword, password, userId } = payload;

    return await RequestService.post(`/users/${userId}/change-password`, {
        controlPassword,
        password,
    });
}

async function authUserWithToken(payload: { token: string; params: string }) {
    const { token, params } = payload;
    return await RequestService.post(
        '/auth/login',
        { token, params: params ? JSON.parse(params) : null },
        { ignoreAuth: true },
    );
}

type OAuthLoginRequest = Omit<OAuthLoginPayload, 'provider'>;

async function authUserWithOAuth(payload: OAuthLoginPayload) {
    const body: OAuthLoginRequest = {
        code: payload.code,
        codeVerifier: payload.codeVerifier,
        redirectUri: payload.redirectUri,
    };

    return await RequestService.post(`/auth/oauth/${payload.provider}`, body, {
        ignoreAuth: true,
    });
}

function isAnonymous() {
    const accessToken = getAccessToken();
    return AuthService.isAnonymous(accessToken);
}
